import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  HandHeart, Clock, Calendar, MapPin, Sparkles, Search,
  CheckCircle2, Loader2,
} from "lucide-react";

/**
 * VolunteerDashboard
 * ------------------
 * Landing page for accounts that signed up as volunteers. Shows:
 *
 *   • Two headline cards — "Events helped" + "Hours logged" (retroactive:
 *     signups made with this email BEFORE the account was created also
 *     count, since volunteers are indexed by email server-side).
 *   • Per-event card list with a Log-hours input that stays inline (no
 *     modal — the flow is trivial enough that one Input + Button is
 *     enough).
 *   • Empty state that punches through to /give-back so a fresh volunteer
 *     knows where to find their first opportunity.
 */
export default function VolunteerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [logging, setLogging] = useState({});   // { signupId: hours }
  const [savingId, setSavingId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/volunteer/my-signups");
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load your volunteer history");
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const logHours = async (id) => {
    const raw = (logging[id] || "").trim();
    const hours = Number(raw);
    if (!raw || !Number.isFinite(hours) || hours <= 0 || hours > 24) {
      toast.error("Enter a number between 0 and 24.");
      return;
    }
    setSavingId(id);
    try {
      await api.post(`/volunteer/signups/${id}/log-hours`, { hours });
      toast.success(`Logged ${hours}h — thank you for volunteering.`);
      setLogging((s) => ({ ...s, [id]: "" }));
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save your hours");
    } finally { setSavingId(null); }
  };

  const totals = data?.totals || { events_helped: 0, hours_logged: 0 };
  const items = data?.items || [];

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 py-10" data-testid="volunteer-dashboard">
      {/* Hero */}
      <div className="flex items-center gap-3">
        <span className="rounded-full p-3"
              style={{ backgroundColor: "rgba(132,204,22,0.15)", color: "#4B7A0A" }}>
          <HandHeart className="h-6 w-6" />
        </span>
        <div>
          <div className="overline" style={{ color: "var(--avt-lime)" }}>Volunteer</div>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight">
            {user?.name ? <>Hi {user.name.split(" ")[0]},</> : <>Welcome,</>}{" "}
            <span style={{ color: "var(--avt-lime)" }}>thanks for showing up.</span>
          </h1>
        </div>
      </div>
      <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
        Every event you help with lives here so you can track your impact.
      </p>

      {/* Rollup cards */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4" data-testid="volunteer-totals">
        <div className="rounded-xl p-4 sm:p-5 border-2"
             style={{ backgroundColor: "rgba(132,204,22,0.06)", borderColor: "rgba(132,204,22,0.35)" }}>
          <div className="overline" style={{ color: "var(--avt-lime)" }}>Events helped</div>
          <div className="font-serif text-4xl mt-1" data-testid="totals-events-helped">
            {totals.events_helped}
          </div>
        </div>
        <div className="rounded-xl p-4 sm:p-5 border-2"
             style={{ backgroundColor: "rgba(44,196,217,0.06)", borderColor: "rgba(44,196,217,0.35)" }}>
          <div className="overline" style={{ color: "var(--avt-teal)" }}>Hours logged</div>
          <div className="font-serif text-4xl mt-1" data-testid="totals-hours-logged">
            {totals.hours_logged}
          </div>
        </div>
      </div>

      {/* CTA to /give-back */}
      <div className="mt-4">
        <Button
          onClick={() => navigate("/give-back")}
          data-testid="find-events-btn"
          variant="outline"
          className="rounded-full px-5 py-2.5 inline-flex items-center gap-2"
        >
          <Search className="h-4 w-4" /> Find events near me
        </Button>
      </div>

      {/* Signup list */}
      <h2 className="font-serif text-2xl mt-10 mb-3">Your events</h2>
      {loading ? (
        <div className="py-12 text-center" data-testid="volunteer-loading">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: "var(--avt-purple)" }} />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed p-8 text-center" data-testid="volunteer-empty"
             style={{ borderColor: "rgba(107,47,186,0.25)" }}>
          <Sparkles className="h-8 w-8 mx-auto" style={{ color: "var(--avt-purple)" }} />
          <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>
            You haven&rsquo;t signed up for any events yet. Browse the Give Back directory to find nearby volunteer opportunities.
          </p>
          <Link to="/give-back" data-testid="volunteer-browse-link"
                className="btn-primary mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full">
            <Search className="h-4 w-4" /> Browse Give Back
          </Link>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="volunteer-list">
          {items.map((row) => {
            const ev = row.event;
            const isPastEvent = ev?.date && new Date(ev.date) < new Date();
            const hasLogged = row.hours_logged > 0;
            return (
              <li key={row.id}
                  data-testid={`volunteer-row-${row.id}`}
                  className="rounded-xl border p-4 sm:p-5 card-warm">
                <div className="flex items-start gap-3">
                  {ev?.cover_image && (
                    <img src={ev.cover_image} alt="Event cover"
                         className="h-16 w-16 rounded-lg object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <Link to={`/events/${ev.id}`}
                          data-testid={`volunteer-row-title-${row.id}`}
                          className="font-serif text-lg hover:underline">
                      {ev?.title || "Event no longer available"}
                    </Link>
                    <div className="mt-1 text-xs flex flex-wrap gap-x-3 gap-y-1"
                         style={{ color: "var(--ink-soft)" }}>
                      {ev?.date && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {new Date(ev.date).toLocaleDateString()}
                        </span>
                      )}
                      {ev?.city && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {ev.city}
                        </span>
                      )}
                    </div>
                  </div>
                  {hasLogged && (
                    <span
                      data-testid={`hours-badge-${row.id}`}
                      className="text-xs inline-flex items-center gap-1 rounded-full px-2 py-1"
                      style={{ backgroundColor: "rgba(132,204,22,0.14)", color: "#4B7A0A" }}
                    >
                      <CheckCircle2 className="h-3 w-3" /> {row.hours_logged}h logged
                    </span>
                  )}
                </div>

                {isPastEvent && !hasLogged && (
                  <div className="mt-3 flex items-end gap-2" data-testid={`log-hours-form-${row.id}`}>
                    <div className="flex-1">
                      <label className="text-xs uppercase tracking-widest"
                             style={{ color: "var(--ink-soft)" }}>
                        <Clock className="inline h-3 w-3 mr-1" /> How many hours did you serve?
                      </label>
                      <Input
                        type="number"
                        min={0.25}
                        max={24}
                        step={0.25}
                        placeholder="3.5"
                        value={logging[row.id] || ""}
                        onChange={(e) => setLogging((s) => ({ ...s, [row.id]: e.target.value }))}
                        data-testid={`log-hours-input-${row.id}`}
                        className="mt-1 max-w-[120px]"
                      />
                    </div>
                    <Button
                      onClick={() => logHours(row.id)}
                      disabled={savingId === row.id || !logging[row.id]}
                      data-testid={`log-hours-save-${row.id}`}
                      className="btn-primary rounded-full px-5 py-2.5 disabled:opacity-60"
                    >
                      {savingId === row.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log hours"}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
